import { ActivatedRouteSnapshot, Params } from '@angular/router';
import { DETAIL_DRAWER_LEVEL_DATA_KEY, DetailDrawerLevel } from './detail-drawer.model';

/** One level of the drawer's path, with the router commands that lead back to it. */
export interface DetailDrawerTrailEntry {
  readonly level: DetailDrawerLevel;
  /** The absolute path of the level: every segment from the root, each with its matrix parameters. */
  readonly commands: readonly (string | Params)[];
}

/** The route of one level of the drawer's path, with the level it carries. */
export interface DetailDrawerLevelRoute {
  readonly snapshot: ActivatedRouteSnapshot;
  readonly level: DetailDrawerLevel;
}

/**
 * The levels of the drawer's path, from the first item to the one on show.
 *
 * @param {ActivatedRouteSnapshot} shellSnapshot - The route the drawer shell stands on
 * @returns {DetailDrawerTrailEntry[]} One entry per level, outermost first
 */
export function buildDetailDrawerTrail(shellSnapshot: ActivatedRouteSnapshot): DetailDrawerTrailEntry[] {
  return findDetailDrawerLevelRoutes(shellSnapshot).map(({ snapshot, level }) => ({ level, commands: buildAbsoluteCommands(snapshot) }));
}

/**
 * The routes of the drawer's levels, from the first item to the one on show. A level stacked over
 * the drawer ends the path: it stands in a shell of its own, which names it.
 *
 * Read from each route's own config rather than its snapshot data: an empty-path child inherits
 * its parent's data, so the snapshot would report every level twice.
 *
 * @param {ActivatedRouteSnapshot} shellSnapshot - The route the drawer shell stands on
 * @returns {DetailDrawerLevelRoute[]} One entry per level, outermost first
 */
export function findDetailDrawerLevelRoutes(shellSnapshot: ActivatedRouteSnapshot): DetailDrawerLevelRoute[] {
  const levelRoutes: DetailDrawerLevelRoute[] = [];
  let snapshot: ActivatedRouteSnapshot | null = shellSnapshot;

  while (snapshot) {
    const level = snapshot.routeConfig?.data?.[DETAIL_DRAWER_LEVEL_DATA_KEY] as DetailDrawerLevel | undefined;
    if (level?.isStacked && snapshot !== shellSnapshot) {
      break;
    }
    if (level) {
      levelRoutes.push({ snapshot, level });
    }
    snapshot = snapshot.firstChild;
  }

  return levelRoutes;
}

/**
 * @param {ActivatedRouteSnapshot} snapshot - The route to lead to
 * @returns {(string | Params)[]} Router commands for its absolute path, matrix parameters included
 */
export function buildAbsoluteCommands(snapshot: ActivatedRouteSnapshot): (string | Params)[] {
  const commands: (string | Params)[] = ['/'];

  for (const routeSnapshot of snapshot.pathFromRoot) {
    for (const segment of routeSnapshot.url) {
      commands.push(segment.path);
      if (Object.keys(segment.parameters).length) {
        commands.push({ ...segment.parameters });
      }
    }
  }

  return commands;
}
